import { useCallback, useEffect, useRef, useState } from 'react'

import { BRAZE_SDK_KEY, BRAZE_SDK_URL, shouldInitializeBraze } from './utils'
import { IS_PRODUCTION } from '../common/constants/constants'
import { sanitizeData } from '../common/utils/userUtils'

import type * as Braze from '@braze/web-sdk'

export const useBrazeSDK = (isBrazeEnabled: boolean, hashedLyraId: string | null) => {
  const [initialized, setInitialized] = useState(false)
  const [identified, setIdentified] = useState(false)
  const brazeRef = useRef<typeof Braze | null>(null)
  const shouldInitialize = shouldInitializeBraze(isBrazeEnabled)

  useEffect(() => {
    if (!shouldInitialize) {
      return
    }
    let cancelled = false
    ;(async () => {
      try {
        // Dynamic import for code splitting - keeps the SDK out of the main bundle
        const braze = await import(/* webpackChunkName: "braze-web-sdk" */ '@braze/web-sdk')
        if (cancelled) {
          return
        }
        braze.initialize(BRAZE_SDK_KEY, {
          baseUrl: BRAZE_SDK_URL,
          enableLogging: !IS_PRODUCTION,
          allowUserSuppliedJavascript: false, // set to `true` to support custom HTML messages
        })
        braze.openSession()
        brazeRef.current = braze
        setInitialized(true)
      } catch (error) {
        console.error('Failed to initialize Braze SDK:', error)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [shouldInitialize])

  useEffect(() => {
    if (hashedLyraId && initialized && brazeRef.current) {
      try {
        brazeRef.current.changeUser(hashedLyraId)
        brazeRef.current.openSession()
        setIdentified(true)
      } catch (error) {
        console.error('Failed to identify user in Braze:', error)
      }
    }
  }, [hashedLyraId, initialized])

  const logCustomEvent = useCallback(
    (event: string, properties?: Record<string, any>) => {
      if (!shouldInitialize) {
        return
      }
      const sanitizedProperties = sanitizeData(properties, ['lyraId', 'lyra_id']) as Record<string, any>
      try {
        brazeRef.current?.logCustomEvent(event, sanitizedProperties)
      } catch (error) {
        console.error('Failed to log custom event in Braze:', error)
      }
    },
    [shouldInitialize],
  )

  const isReady = initialized && (!hashedLyraId || identified)

  return {
    isReady,
    logCustomEvent,
  }
}
